package one.devsbox.www.ui.widgets

import androidx.compose.animation.core.*
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import kotlin.math.abs

@Composable
fun FullscreenLoadingUI(
    color: Color = Color.Gray
) {
    val lineFraction = remember { Animatable(1f) }
    var isRevealed by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        // at start reveal effect: horizontal line going bottom (1f) to top (0f) in 500ms
        lineFraction.animateTo(
            targetValue = 0f,
            animationSpec = spring(stiffness = Spring.StiffnessVeryLow)
        )
        isRevealed = true

        // then starts repeating from bottom to top on a timer of 3000ms
        while (true) {
//            lineFraction.snapTo(1f)
            lineFraction.animateTo(
                targetValue = 1f,
                animationSpec = tween(durationMillis = 3000, easing = LinearEasing)
            )
            lineFraction.animateTo(
                targetValue = 0f,
                animationSpec = tween(durationMillis = 3000, easing = LinearEasing)
            )
        }
    }

    val yOffsetFraction = lineFraction.value
    val backgroundColor = MaterialTheme.colorScheme.background
    val scanlineColor = color

    Box(modifier = Modifier.fillMaxSize().background(backgroundColor)) {
        Canvas(modifier = Modifier.fillMaxSize()) {
            val canvasWidth = size.width
            val canvasHeight = size.height
            val yPos = canvasHeight * yOffsetFraction

            // 1. Draw Sci-Fi Cybernetic Grid Background
            val gridSpacing = 40.dp.toPx()

            // Vertical Grid Lines
            val numCols = (canvasWidth / gridSpacing).toInt()
            for (col in 0..numCols) {
                val x = col * gridSpacing
                val startY = if (isRevealed) 0f else yPos
                drawLine(
                    color = scanlineColor.copy(alpha = 0.1f),
                    start = Offset(x, startY),
                    end = Offset(x, canvasHeight),
                    strokeWidth = 1f
                )
            }

            // Horizontal Grid Lines with reactive sweep glow
            val numRows = (canvasHeight / gridSpacing).toInt()
            for (row in 0..numRows) {
                val y = row * gridSpacing
                if (!isRevealed && y < yPos) continue

                val distance = abs(y - yPos)
                val highlightAlpha = (1f - (distance / 150.dp.toPx()).coerceIn(0f, 1f)) * 0.12f
                val rowColor = scanlineColor.copy(alpha = 0.02f + highlightAlpha)
                drawLine(
                    color = rowColor,
                    start = Offset(0f, y),
                    end = Offset(canvasWidth, y),
                    strokeWidth = if (highlightAlpha > 0.02f) 1.5f else 1f
                )
            }

            // 2. Draw Multi-Layer Glowing Scanline

            // Layer A: Wide Ambient Glow (120dp height)
            val glowHeightA = 120.dp.toPx()
            val brushA = Brush.verticalGradient(
                colors = listOf(
                    Color.Transparent,
                    scanlineColor.copy(alpha = 0.03f),
                    scanlineColor.copy(alpha = 0.03f),
                    Color.Transparent
                ),
                startY = yPos - glowHeightA,
                endY = yPos + glowHeightA
            )
            drawRect(
                brush = brushA,
                topLeft = Offset(0f, yPos - glowHeightA),
                size = Size(canvasWidth, glowHeightA * 2)
            )

            // Layer B: Medium Scanline Trail (40dp height)
            val glowHeightB = 40.dp.toPx()
            val brushB = Brush.verticalGradient(
                colors = listOf(
                    Color.Transparent,
                    scanlineColor.copy(alpha = 0.15f),
                    scanlineColor.copy(alpha = 0.15f),
                    Color.Transparent
                ),
                startY = yPos - glowHeightB,
                endY = yPos + glowHeightB
            )
            drawRect(
                brush = brushB,
                topLeft = Offset(0f, yPos - glowHeightB),
                size = Size(canvasWidth, glowHeightB * 2)
            )

            // Layer C: Intense Core Glow (12dp height)
            val glowHeightC = 12.dp.toPx()
            val brushC = Brush.verticalGradient(
                colors = listOf(
                    Color.Transparent,
                    scanlineColor.copy(alpha = 0.5f),
                    scanlineColor.copy(alpha = 0.5f),
                    Color.Transparent
                ),
                startY = yPos - glowHeightC,
                endY = yPos + glowHeightC
            )
            drawRect(
                brush = brushC,
                topLeft = Offset(0f, yPos - glowHeightC),
                size = Size(canvasWidth, glowHeightC * 2)
            )

            // 3. Draw Laser Edge Calibration Dots (Left & Right margins)
            // Left Dot
            drawCircle(
                color = scanlineColor.copy(alpha = 0.2f),
                radius = 8.dp.toPx(),
                center = Offset(4.dp.toPx(), yPos)
            )
            drawCircle(
                color = scanlineColor,
                radius = 3.dp.toPx(),
                center = Offset(4.dp.toPx(), yPos)
            )

            // Right Dot
            drawCircle(
                color = scanlineColor.copy(alpha = 0.2f),
                radius = 8.dp.toPx(),
                center = Offset(canvasWidth - 4.dp.toPx(), yPos)
            )
            drawCircle(
                color = scanlineColor,
                radius = 3.dp.toPx(),
                center = Offset(canvasWidth - 4.dp.toPx(), yPos)
            )

            // 4. Draw Core Sharp Solid Laser Line
            drawLine(
                color = scanlineColor,
                start = Offset(0f, yPos),
                end = Offset(canvasWidth, yPos),
                strokeWidth = 1.5.dp.toPx()
            )
        }
    }
}